// B01 — NEGOCIOS (Sección 1 · Tests 2, 3, 4 · retail-org-business).
// Construida con el KIT del dominio (domains/retail/components/, DEC-SKF1-015): SectionHeader,
// ListToolbar+SearchInput, EntityCard+StatusBadge+MetaLine+CardActions, ListFooterActions.
// Mock: M6_ADMIN §2. Los bloques "Ventas este mes" / "Cobros hoy" y la acción [Métricas] son F2/F3
// sin fuente en F1 → OMITIDOS (DEC-SKF1-013). Actions del mock: [Editar] y [Vendedores] abren el
// detalle (zonas/tiendas/usuarios); [Inventario] navega al catálogo (B02).
import { useMemo, useState } from 'react';
import { Plus, Store } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button, EmptyState, Loading } from '@/kit';
import {
    CardActions,
    EntityCard,
    EntityDetailModal,
    ListFooterActions,
    ListToolbar,
    MetaLine,
    SearchInput,
    SectionHeader,
    StatusBadge,
    type StatusConfig,
} from '../components';
import { RetailerStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailerStatusEnum';
import { RetailerTypeEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailerTypeEnum';
import { getRetailer } from '@/api/services/retail-org/retail-org.client';
import { useChangeRetailerStatus, useRetailers, type Retailer } from './api/useRetailOrg';
import { RetailerFormModal } from './components/RetailerFormModal';
import { RetailerDetailModal } from './components/RetailerDetailModal';

const RETAILER_STATUS: Record<string, StatusConfig> = {
    [RetailerStatusEnum.ACTIVE]: { label: 'Activo', variant: 'success' },
    [RetailerStatusEnum.SUSPENDED]: { label: 'Suspendido', variant: 'warning' },
    [RetailerStatusEnum.INACTIVE]: { label: 'Inactivo', variant: 'secondary' },
};

const TYPE_LABEL: Record<string, string> = {
    [RetailerTypeEnum.CHAIN]: 'Cadena',
    [RetailerTypeEnum.SUBDISTRIBUTOR]: 'Subdistribuidor',
    [RetailerTypeEnum.FRANCHISE]: 'Franquicia',
};

interface DetailTarget {
    retailer: Retailer;
    tab: string;
}

export function NegociosPage() {
    const navigate = useNavigate();
    const [createOpen, setCreateOpen] = useState(false);
    const [editRetailer, setEditRetailer] = useState<Retailer | null>(null);
    const [detail, setDetail] = useState<DetailTarget | null>(null);
    const [viewRecord, setViewRecord] = useState<Record<string, unknown> | null>(null);
    const [subdistParent, setSubdistParent] = useState<Retailer | null>(null);
    const [search, setSearch] = useState('');
    const { data, isLoading, isError, error } = useRetailers();
    const changeStatus = useChangeRetailerStatus();

    const retailers: Retailer[] = useMemo(() => data ?? [], [data]);

    const counts = useMemo(() => {
        const active = retailers.filter((r) => r.status === RetailerStatusEnum.ACTIVE).length;
        return { active, inactive: retailers.length - active };
    }, [retailers]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return retailers;
        return retailers.filter(
            (r) =>
                r.name.toLowerCase().includes(q) ||
                r.legalName.toLowerCase().includes(q) ||
                r.rfc.toLowerCase().includes(q),
        );
    }, [retailers, search]);

    const toggleStatus = (retailer: Retailer) => {
        const next =
            retailer.status === RetailerStatusEnum.ACTIVE
                ? RetailerStatusEnum.INACTIVE
                : RetailerStatusEnum.ACTIVE;
        changeStatus.mutate(
            { retailerId: retailer.retailerId, status: next },
            {
                onSuccess: () => toast.success('Estado actualizado'),
                onError: (e) =>
                    toast.error(e instanceof Error ? e.message : 'No se pudo cambiar el estado'),
            },
        );
    };

    return (
        <div className="space-y-4">
            <SectionHeader
                title="NEGOCIOS"
                counters={[
                    { label: `${String(counts.active)} activos`, variant: 'success' },
                    { label: `${String(counts.inactive)} inactivos`, variant: 'secondary' },
                ]}
                actions={
                    <Button
                        leftIcon={<Plus aria-hidden />}
                        onClick={() => { setCreateOpen(true); }}
                    >
                        Nuevo negocio
                    </Button>
                }
                divider
            />

            <ListToolbar
                search={
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder="Buscar por nombre, razón social o RFC…"
                    />
                }
            />

            {isLoading ? (
                <Loading text="Cargando negocios…" />
            ) : isError ? (
                <EmptyState
                    icon={<Store className="size-8" aria-hidden />}
                    title="No se pudieron cargar los negocios"
                    description={error instanceof Error ? error.message : 'Error desconocido'}
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Store className="size-8" aria-hidden />}
                    title="Sin negocios"
                    description="Crea el primer negocio con el botón «Nuevo negocio»."
                />
            ) : (
                <div className="space-y-2">
                    {filtered.map((retailer) => (
                        <EntityCard
                            key={retailer.retailerId}
                            title={retailer.name}
                            status={<StatusBadge value={retailer.status} config={RETAILER_STATUS} />}
                            aside={
                                <Button
                                    variant="outline"
                                    size="sm"
                                    isLoading={changeStatus.isPending && changeStatus.variables.retailerId === retailer.retailerId}
                                    onClick={() => { toggleStatus(retailer); }}
                                >
                                    {retailer.status === RetailerStatusEnum.ACTIVE
                                        ? 'Desactivar'
                                        : 'Activar'}
                                </Button>
                            }
                            meta={
                                <MetaLine
                                    items={[
                                        TYPE_LABEL[retailer.type] ?? retailer.type,
                                        retailer.legalName,
                                        retailer.rfc,
                                    ]}
                                />
                            }
                            actions={
                                <CardActions
                                    actions={[
                                        {
                                            label: 'Ver',
                                            onClick: () => {
                                                void getRetailer(retailer.retailerId)
                                                    .then((r) => {
                                                        setViewRecord(r as unknown as Record<string, unknown>);
                                                    })
                                                    .catch((e: unknown) => {
                                                        toast.error(
                                                            e instanceof Error ? e.message : 'No se pudo cargar',
                                                        );
                                                    });
                                            },
                                        },
                                        {
                                            label: 'Editar',
                                            onClick: () => { setEditRetailer(retailer); },
                                        },
                                        {
                                            label: 'Vendedores',
                                            onClick: () => { setDetail({ retailer, tab: 'usuarios' }); },
                                        },
                                        {
                                            label: 'Inventario',
                                            onClick: () => {
                                                void navigate({
                                                    to: '/seccion/$key',
                                                    params: { key: 'catalogo' },
                                                });
                                            },
                                        },
                                        {
                                            label: '+ Subdist',
                                            onClick: () => { setSubdistParent(retailer); },
                                        },
                                    ]}
                                />
                            }
                        />
                    ))}
                </div>
            )}

            <ListFooterActions>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { toast.info('Exportar: próximamente'); }}
                >
                    Exportar
                </Button>
            </ListFooterActions>

            <RetailerFormModal isOpen={createOpen} onClose={() => { setCreateOpen(false); }} />
            <RetailerFormModal
                key={editRetailer?.retailerId ?? 'new-edit'}
                isOpen={!!editRetailer}
                onClose={() => { setEditRetailer(null); }}
                editRetailer={editRetailer}
            />
            <RetailerDetailModal
                key={detail ? `${detail.retailer.retailerId}-${detail.tab}` : 'closed'}
                retailer={detail?.retailer ?? null}
                initialTab={detail?.tab}
                onClose={() => { setDetail(null); }}
            />
            <RetailerFormModal
                key={subdistParent?.retailerId ?? 'no-subdist'}
                isOpen={!!subdistParent}
                onClose={() => { setSubdistParent(null); }}
                parentId={subdistParent?.retailerId}
            />
            <EntityDetailModal
                title="Detalle del negocio"
                record={viewRecord}
                onClose={() => { setViewRecord(null); }}
            />
        </div>
    );
}
